import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

interface PromoCode {
  id: string;
  code: string;
  entitlementId: string;
  tier: 'plus' | 'premium';
  grantExpiresAt: string;
  maxUses: number | null;
  active: boolean;
  redemptionCount: number;
  createdAt: string;
  updatedAt: string;
}

interface CreatePromoRequest {
  code: string;
  entitlementId: string;
  tier: 'plus' | 'premium';
  grantExpiresAt: string;
  maxUses: number | null;
}

async function fetchPromoCodes() {
  const response = await fetch('/api/v1/admin/promo-codes');
  if (!response.ok) {
    throw new Error('Failed to fetch promo codes');
  }
  return response.json() as Promise<{ codes: PromoCode[] }>;
}

async function createPromoCode(request: CreatePromoRequest) {
  const response = await fetch('/api/v1/admin/promo-codes', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (!response.ok) {
    throw new Error('Failed to create promo code');
  }
  return response.json();
}

async function deactivatePromoCode(code: string) {
  const response = await fetch(`/api/v1/admin/promo-codes/${code}/deactivate`, {
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error('Failed to deactivate promo code');
  }
  return response.json();
}

export default function PromosPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-promo-codes'],
    queryFn: fetchPromoCodes,
  });

  const createMutation = useMutation({
    mutationFn: createPromoCode,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-promo-codes'] });
      setShowCreateDialog(false);
      setNewPromo({
        code: '',
        entitlementId: 'plus',
        tier: 'plus',
        grantExpiresAt: '',
        maxUses: null,
      });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: deactivatePromoCode,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-promo-codes'] });
    },
  });

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newPromo, setNewPromo] = useState<CreatePromoRequest>({
    code: '',
    entitlementId: 'plus',
    tier: 'plus',
    grantExpiresAt: '',
    maxUses: null,
  });

  const handleCreate = () => {
    if (!newPromo.code || newPromo.code.length < 3 || newPromo.code.length > 64) {
      alert('Code must be between 3 and 64 characters');
      return;
    }
    if (!newPromo.grantExpiresAt || isNaN(Date.parse(newPromo.grantExpiresAt))) {
      alert('Valid expiration date required');
      return;
    }
    createMutation.mutate(newPromo);
  };

  const handleDeactivate = (code: string) => {
    if (confirm(`Deactivate promo code "${code}"?`)) {
      deactivateMutation.mutate(code);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
            Promo Codes
          </h2>
          <p className="mt-2 text-sm text-gray-500">Manage promotional codes</p>
        </div>
        <button
          onClick={() => setShowCreateDialog(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
        >
          Create Promo Code
        </button>
      </div>

      <div className="mt-6 bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <p className="mt-2 text-sm text-gray-500">Loading promo codes...</p>
          </div>
        ) : error ? (
          <div className="p-6">
            <div className="rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">
                Error loading promo codes: {error.message}
              </p>
            </div>
          </div>
        ) : !data || data.codes.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="mt-2 text-sm">No promo codes found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">
                    Code
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Tier
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Expires
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Redemptions
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Status
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Created
                  </th>
                  <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {data.codes.map((promo) => (
                  <tr key={promo.id}>
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                      {promo.code}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                        promo.tier === 'premium'
                          ? 'bg-purple-50 text-purple-700 ring-purple-600/20'
                          : 'bg-green-50 text-green-700 ring-green-600/20'
                      }`}>
                        {promo.tier}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {new Date(promo.grantExpiresAt).toLocaleDateString()}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {promo.redemptionCount}
                      {promo.maxUses && ` / ${promo.maxUses}`}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      {promo.active ? (
                        <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {new Date(promo.createdAt).toLocaleDateString()}
                    </td>
                    <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                      {promo.active && (
                        <button
                          onClick={() => handleDeactivate(promo.code)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Deactivate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Dialog */}
      {showCreateDialog && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Promo Code</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Code</label>
                <input
                  type="text"
                  value={newPromo.code}
                  onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
                  placeholder="PROMO123"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Tier</label>
                <select
                  value={newPromo.tier}
                  onChange={(e) => setNewPromo({ ...newPromo, tier: e.target.value as 'plus' | 'premium' })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
                >
                  <option value="plus">Plus</option>
                  <option value="premium">Premium</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Grant Expires At</label>
                <input
                  type="datetime-local"
                  value={newPromo.grantExpiresAt}
                  onChange={(e) => setNewPromo({ ...newPromo, grantExpiresAt: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Max Uses (optional)</label>
                <input
                  type="number"
                  min="1"
                  value={newPromo.maxUses || ''}
                  onChange={(e) => setNewPromo({ ...newPromo, maxUses: e.target.value ? parseInt(e.target.value) : null })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
                  placeholder="Unlimited"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateDialog(false);
                  setNewPromo({
                    code: '',
                    entitlementId: 'plus',
                    tier: 'plus',
                    grantExpiresAt: '',
                    maxUses: null,
                  });
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={createMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {createMutation.isPending ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
